import { TableObject } from "./common/baseTableTypes";
import {
  BaseFullSchema_OLD,
  BaseCreateSchema,
  IdAsUUID,
  AddrData,
  HoldableMixin,
  SCH_BaseFull,
} from "./common/commonTypes";
import * as yup from "yup";
import { TypedSelectOption } from "./common/selectOptions";
import {
  YupBaseCreateSchema,
  YupTableObject,
  YupTypedSelectOption_v2,
} from "./common/yupTypes";
import stringUtils from "../utils/stringUtils";
import { DefaultValues } from "react-hook-form";
import { VoidableMixin } from "./common/commonTypes";
import { ShipmentStatus } from "./shipmentsTypes";

export const COMPLAINT_CATEGORIES = [
  "ALARM",
  "CHEST_TUBE",
  "CONTROL_MODULE",
  "DAMAGE",
  "DRAINAGE_KIT",
  "POWER_SUPPLY",
  "OTHER",
] as const;
export type ComplaintCategoryType = (typeof COMPLAINT_CATEGORIES)[number];

type ComplaintProductDataEntry = [string, IdAsUUID | null, number, boolean];

export interface ComplaintProductData {
  TGCM: ComplaintProductDataEntry[];
  TGDK: ComplaintProductDataEntry[];
  TGCT20: ComplaintProductDataEntry[];
  TGCT28: ComplaintProductDataEntry[];
  TGPS: ComplaintProductDataEntry[];
}

export interface ComplaintFull extends BaseFullSchema_OLD {
  cf_label: string;
  cf_number: number;
  date_of_occurrence: string;
  cf_category: ComplaintCategoryType;
  cat_other: string | null;
  issue_summary: string;
  nature_of_issue: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  customer_id: IdAsUUID;
  rma_addr: AddrData | null;
  date_reported: string;
  reported_to: IdAsUUID;
  product_data: ComplaintProductData;
  to_be_returned: boolean;
  box_file_id: string | null;
  gl_cf_id: IdAsUUID | null;
  gl_cf_display_id: string | null;
  gl_cf_approved_id: string | null;
  gl_cf_num: string | null;
  approved: boolean;
  submitted_by: IdAsUUID;
}

export interface ComplaintCreate extends BaseCreateSchema {
  cf_label: string;
  date_of_occurrence: string;
  cf_category: ComplaintCategoryType;
  cat_other: string | null;
  issue_summary: string;
  nature_of_issue: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  customer_id: IdAsUUID;
  rma_addr: AddrData | null;
  date_reported: string;
  reported_to: IdAsUUID;
  product_data: ComplaintProductData;
  submitted_by: IdAsUUID;
}

export interface ComplaintUpdate extends Partial<
  Omit<ComplaintCreate, "submitted_by">
> {}

export type RMAFormMode =
  | "create"
  | "createbatch"
  | "view"
  | "edit"
  | "receive"
  | "receivebatch";

export const RMA_STATUSES = [
  "AWAITING_CENTESE_CONFIRMATION",
  "AWAITING_CUSTOMER_CONFIRMATION",
  "AWAITING_RECEIPT",
  "AWAITING_INSPECTION",
  "INSPECTION_IN_PROGRESS",
  "AWAITING_REPAIR",
  "REPAIRS_IN_PROGRESS",
  "AWAITING_REVIEW",
  "AWAITING_SHIPMENT",
  "COMPLETED",
] as const;
export type RMAStatusType = (typeof RMA_STATUSES)[number];

export const RMA_REASONS = [
  "CLINICAL_UNIT",
  "END_OF_DEMO",
  "LOANER_RETURN",
  "OTHER",
  "OVERPURCHASE",
  "REPLACEMENT",
  "SERVICE",
] as const;
export type RMAReasonType = (typeof RMA_REASONS)[number];

// RMA Select Types
export type RMACFOption = TypedSelectOption<
  "customer_feedback",
  {
    nuc_cf_num: string | null;
    nuc_cf_label: string | null;
    gl_cf: string | null;
    cf_cust_id: IdAsUUID;
  },
  IdAsUUID
>;
export type RMACustOption = TypedSelectOption<
  "customer",
  { code: string; name: string },
  IdAsUUID
>;
export type RMAItemOption = TypedSelectOption<
  "item",
  { number: string; name: string; catalog: string },
  IdAsUUID
>;
export type RMALotOption = TypedSelectOption<
  "lot",
  {
    item_id: IdAsUUID;
  },
  IdAsUUID
>;
export type RMACMOption = TypedSelectOption<"control_module", {}, IdAsUUID>;
export type RMAShipmentOption = TypedSelectOption<"shipment", {}, IdAsUUID>;
export type RMAReasonOption = TypedSelectOption<
  "rma_reason",
  {},
  RMAReasonType
>;
export const RMA_REASON_OPTIONS: RMAReasonOption[] = RMA_REASONS.map(
  (value) => ({
    label: stringUtils.capFirstLetter(value.replaceAll("_", " ")),
    type: "rma_reason",
    value: {
      id: value as RMAReasonType,
    },
  }),
);
export type RMAOption = TypedSelectOption<
  "rma_record",
  { item: RMAItemOption; lot: RMALotOption | null; quantity: number },
  IdAsUUID
>;

export interface RMA_FULL extends SCH_BaseFull, VoidableMixin, HoldableMixin {
  cf_id: IdAsUUID | null;
  nuc_cf_num: string | null;
  nuc_cf_label: string | null;
  gl_cf: string | null;
  cf_cust_id: string | null;

  cust_id: IdAsUUID;
  cust_name: string;
  cust_code: string;

  item_id: IdAsUUID;
  item_catalog: string;
  item_num: string;
  item_name: string;

  lot_id: IdAsUUID | null;
  lot_num: string | null;
  lot_item_id: IdAsUUID | null;

  loaner_id: IdAsUUID | null;
  loaner_num: string | null;

  replacement_id: IdAsUUID | null;
  replacement_num: string | null;

  shipment_inbound: IdAsUUID | null;
  shipment_inbound_status: ShipmentStatus | null;
  shipment_loaner: IdAsUUID | null;
  shipment_loaner_status: ShipmentStatus | null;
  shipment_return: IdAsUUID | null;
  shipment_return_status: ShipmentStatus | null;

  number: number;
  reason_other: string | null;
  rma_details: string;

  quantity: number;
  quantity_received: number | null;

  date_opened: Date;
  date_received: Date | null;
  date_inspected: Date | null;
  date_estimated_completion: Date | null;
  date_closed: Date | null;

  status: RMAStatusType;
  reason: RMAReasonType;

  contact: TableObject<"contact">;
  address: TableObject<"orderaddress">;
  return_addr: TableObject<"orderaddress"> | null;
}

export interface RMAFormDataBatchCreateEntry {
  item: RMAItemOption | null;
  lot: RMALotOption | null;
  reason: RMAReasonOption | null;

  reason_other: string | null;
  rma_details: string;

  quantity: number;
}

export const RMAValidationSchemaBatchCreateEntry = yup.object({
  item: YupTypedSelectOption_v2(
    "item",
    yup.object({
      id: yup.string().defined().required(),
      number: yup.string().defined().required(),
      name: yup.string().defined().required(),
      catalog: yup.string().defined().required(),
    }),
  )
    .defined()
    .required("Item is required"),
  lot: YupTypedSelectOption_v2(
    "lot",
    yup.object({
      id: yup.string().defined().required(),
      item_id: yup.string().defined().required(),
    }),
  )
    .defined()
    .nullable(),
  reason: YupTypedSelectOption_v2(
    "rma_reason",
    yup.object({
      id: yup.string().defined().equals(RMA_REASONS).required(),
    }),
  )
    .defined()
    .required("Must select a reason for return."),
  reason_other: yup
    .string()
    .defined()
    .transform((val, orig) => {
      if (orig === "") {
        return null;
      } else {
        return val;
      }
    })
    .nullable()
    .test({
      name: "reason-other",
      test: function (value, context) {
        if (
          context.parent?.reason?.value.id === ("OTHER" as RMAReasonType) &&
          !value
        ) {
          return context.createError({
            message:
              "Must provide context when selecting OTHER as the reason for return.",
          });
        }
        return true;
      },
    }),
  rma_details: yup.string().defined().required("Details are required."),
  quantity: yup
    .number()
    .defined()
    // eslint-disable-next-line no-template-curly-in-string
    .min(1, "Quantity must be at least ${min}.")
    .required("Quantity is required."),
});

export interface RMARequestDataBatchCreateEntry {
  item: IdAsUUID;
  lot: IdAsUUID | null;
  reason: RMAReasonType;

  reason_other: string | null;
  rma_details: string;

  quantity: number;
}

export interface RMAFormDataBatchCreateGroup {
  customer_feedback: RMACFOption | null;
  create_cf: boolean;
  customer: RMACustOption | null;

  date_opened: Date | null;

  contact: TableObject<"contact">;
  address: TableObject<"orderaddress">;
  return_addr: TableObject<"orderaddress"> | null;
  entries: RMAFormDataBatchCreateEntry[];
}

export const RMAValidationSchemaBatchCreateGroup = yup.object({
  customer_feedback: YupTypedSelectOption_v2(
    "customer_feedback",
    yup.object({
      id: yup.string().defined().required(),
      nuc_cf_num: yup.string().defined().required(),
      nuc_cf_label: yup.string().defined().required(),
      gl_cf: yup.string().defined().nullable(),
      cf_cust_id: yup.string().defined().required(),
    }),
  )
    .defined()
    .nullable(),
  create_cf: yup.boolean().defined().required(),
  customer: YupTypedSelectOption_v2(
    "customer",
    yup.object({
      id: yup.string().defined().required(),
      code: yup.string().defined().required(),
      name: yup.string().defined().required(),
    }),
  )
    .defined()
    .required("Must select a Customer for this RMA."),
  date_opened: yup.date().defined().nullable(),
  contact: YupTableObject("contact", { email: true })
    .defined()
    .required("Contact is required."),
  address: YupTableObject("orderaddress")
    .defined()
    .required("Initial RMA Address is required."),
  return_addr: YupTableObject("orderaddress").defined().nullable(),
  entries: yup
    .array()
    .of(RMAValidationSchemaBatchCreateEntry)
    // eslint-disable-next-line no-template-curly-in-string
    .min(1, "Must create at least ${min} new RMAs for this Group.")
    .defined()
    .required(),
});

export interface RMARequestDataBatchCreateGroup {
  customer_feedback: IdAsUUID | null;
  create_cf: boolean;
  customer: IdAsUUID;

  date_opened: Date | null;

  contact: TableObject<"contact">;
  address: TableObject<"orderaddress">;
  return_addr: TableObject<"orderaddress"> | null;
  entries: RMARequestDataBatchCreateEntry[];
}

export interface RMAFormDataBatchCreateFull {
  rma_data: RMAFormDataBatchCreateGroup[];
}

export const RMAValidationSchemaBatchCreateFull = yup.object({
  rma_data: yup
    .array()
    .of(RMAValidationSchemaBatchCreateGroup)
    // eslint-disable-next-line no-template-curly-in-string
    .min(1, "Must create at least ${min} new RMAs")
    .defined()
    .required(),
});

export interface RMARequestDataBatchCreateFull {
  rma_data: RMARequestDataBatchCreateGroup[];
}

export interface RMAFormDataCreate {
  customer_feedback: RMACFOption | null;
  customer: RMACustOption | null;
  item: RMAItemOption | null;
  lot: RMALotOption | null;
  reason: RMAReasonOption | null;

  reason_other: string | null;
  rma_details: string;

  quantity: number;

  date_opened: Date;

  contact: TableObject<"contact">;
  address: TableObject<"orderaddress">;
  return_addr: TableObject<"orderaddress"> | null;
}

export const RMAValidationSchemaCreate = yup.object({
  customer_feedback: YupTypedSelectOption_v2(
    "customer_feedback",
    yup.object({
      id: yup.string().defined().required(),
      nuc_cf_num: yup.string().defined().required(),
      nuc_cf_label: yup.string().defined().required(),
      gl_cf: yup.string().defined().nullable(),
      cf_cust_id: yup.string().defined().required(),
    }),
  )
    .defined()
    .nullable(),
  customer: YupTypedSelectOption_v2(
    "customer",
    yup.object({
      id: yup.string().defined().required(),
      code: yup.string().defined().required(),
      name: yup.string().defined().required(),
    }),
  )
    .defined()
    .required("Customer is required."),
  item: YupTypedSelectOption_v2(
    "item",
    yup.object({
      id: yup.string().defined().required(),
      number: yup.string().defined().required(),
      name: yup.string().defined().required(),
      catalog: yup.string().defined().required(),
    }),
  )
    .defined()
    .required("Item is required"),
  lot: YupTypedSelectOption_v2(
    "lot",
    yup.object({
      id: yup.string().defined().required(),
      item_id: yup.string().defined().required(),
    }),
  )
    .defined()
    .nullable(),
  reason: YupTypedSelectOption_v2(
    "rma_reason",
    yup.object({
      id: yup.string().defined().equals(RMA_REASONS).required(),
    }),
  )
    .defined()
    .required("RMA Reason is required."),
  reason_other: yup
    .string()
    .defined()
    .transform((val, orig) => {
      if (orig === "") {
        return null;
      } else {
        return val;
      }
    })
    .nullable()
    .test({
      name: "reason-other",
      test: function (value, context) {
        if (
          context.parent?.reason?.value.id === ("OTHER" as RMAReasonType) &&
          !value
        ) {
          return context.createError({
            message:
              "Must provide context when selecting OTHER as the reason for return.",
          });
        }
        return true;
      },
    }),
  rma_details: yup.string().defined().required("Details are required."),
  quantity: yup
    .number()
    .defined()
    // eslint-disable-next-line no-template-curly-in-string
    .min(1, "Quantity must be at least ${min}.")
    .required("Quantity is required."),
  date_opened: yup.date().defined().required("Date Opened is required."),
  contact: YupTableObject("contact", { email: true })
    .defined()
    .required("Contact is required."),
  address: YupTableObject("orderaddress")
    .defined()
    .required("Initial RMA Address is required."),
  return_addr: YupTableObject("orderaddress").defined().nullable(),
});

export interface RMARequestDataCreate {
  customer_feedback: IdAsUUID | null;
  customer: IdAsUUID;
  item: IdAsUUID;
  lot: IdAsUUID | null;
  reason: RMAReasonType;

  reason_other: string | null;
  rma_details: string;

  quantity: number;
  quantity_recieved: number;

  date_opened: Date;

  contact: TableObject<"contact">;
  address: TableObject<"orderaddress">;
  return_addr: TableObject<"orderaddress"> | null;
}

export interface RMAFormDataUpdate {
  customer_feedback: RMACFOption | null;
  customer: RMACustOption | null;
  item: RMAItemOption | null;
  lot: RMALotOption | null;
  reason: RMAReasonOption | null;

  reason_other: string | null;
  rma_details: string;

  quantity: number;

  date_opened: Date;
  date_inspected: Date | null;
  date_estimated_completion: Date | null;
  date_closed: Date | null;

  contact: TableObject<"contact">;
  address: TableObject<"orderaddress">;
  return_addr: TableObject<"orderaddress"> | null;
}

export const RMAValidationSchemaUpdate = yup.object({
  customer_feedback: YupTypedSelectOption_v2(
    "customer_feedback",
    yup.object({
      id: yup.string().defined().required(),
      nuc_cf_num: yup.string().defined().required(),
      nuc_cf_label: yup.string().defined().required(),
      gl_cf: yup.string().defined().nullable(),
      cf_cust_id: yup.string().defined().required(),
    }),
  )
    .defined()
    .nullable(),
  customer: YupTypedSelectOption_v2(
    "customer",
    yup.object({
      id: yup.string().defined().required(),
      code: yup.string().defined().required(),
      name: yup.string().defined().required(),
    }),
  )
    .defined()
    .required("Customer is required."),
  item: YupTypedSelectOption_v2(
    "item",
    yup.object({
      id: yup.string().defined().required(),
      number: yup.string().defined().required(),
      name: yup.string().defined().required(),
      catalog: yup.string().defined().required(),
    }),
  )
    .defined()
    .required("Item is required"),
  lot: YupTypedSelectOption_v2(
    "lot",
    yup.object({
      id: yup.string().defined().required(),
      item_id: yup.string().defined().required(),
    }),
  )
    .defined()
    .nullable(),
  reason: YupTypedSelectOption_v2(
    "rma_reason",
    yup.object({
      id: yup.string().defined().equals(RMA_REASONS).required(),
    }),
  )
    .defined()
    .required("RMA Reason is required."),
  reason_other: yup
    .string()
    .defined()
    .transform((val, orig) => {
      if (orig === "") {
        return null;
      } else {
        return val;
      }
    })
    .nullable()
    .test({
      name: "reason-other",
      test: function (value, context) {
        if (
          context.parent?.reason?.value.id === ("OTHER" as RMAReasonType) &&
          !value
        ) {
          return context.createError({
            message:
              "Must provide context when selecting OTHER as the reason for return.",
          });
        }
        return true;
      },
    }),
  rma_details: yup.string().defined().required("Details are required."),
  quantity: yup
    .number()
    .defined()
    // eslint-disable-next-line no-template-curly-in-string
    .min(1, "Quantity must be at least ${min}.")
    .required("Quantity is required."),
  date_opened: yup.date().defined().required("Date Opened is required."),
  date_inspected: yup.date().defined().nullable(),
  date_estimated_completion: yup.date().defined().nullable(),
  date_closed: yup.date().defined().nullable(),
  contact: YupTableObject("contact", { email: true })
    .defined()
    .required("Contact is required."),
  address: YupTableObject("orderaddress")
    .defined()
    .required("Initial RMA Address is required."),
  return_addr: YupTableObject("orderaddress").defined().nullable(),
});

export interface RMARequestDataUpdate {
  customer_feedback?: IdAsUUID | null;
  customer?: IdAsUUID;
  item?: IdAsUUID;
  lot?: IdAsUUID | null;
  reason?: RMAReasonType;

  reason_other?: string | null;
  rma_details?: string;

  quantity?: number;

  date_opened?: Date;
  date_inspected?: Date | null;
  date_estimated_completion?: Date | null;
  date_closed?: Date | null;

  contact?: TableObject<"contact">;
  address?: TableObject<"orderaddress">;
  return_addr?: TableObject<"orderaddress"> | null;
}

export interface RMAFormDataReceive {
  rma: RMAOption | null;
  item: RMAItemOption | null;
  lot: RMALotOption | null;
  quantity: number;
  date: Date;
}

export const RMAValidationSchemaReceive = yup.object({
  rma: YupTypedSelectOption_v2(
    "rma_record",
    yup.object({
      id: yup.string().defined().required(),
      lot: YupTypedSelectOption_v2(
        "lot",
        yup.object({
          id: yup.string().defined().required(),
        }),
      )
        .defined()
        .nullable(),
    }),
  )
    .defined()
    .required("Received RMA is required."),
  item: YupTypedSelectOption_v2(
    "item",
    yup.object({
      id: yup.string().defined().required(),
    }),
  )
    .defined()
    .required("Received item is required."),
  lot: YupTypedSelectOption_v2(
    "lot",
    yup.object({
      id: yup.string().defined().required(),
    }),
  )
    .defined()
    .required("Received lot is required."),
  quantity: yup.number().defined().required("Quantity received is required."),
  date: yup.date().defined().required("Date recevied is required."),
});

export interface RMARequestDataReceive {
  rma: IdAsUUID;
  item: IdAsUUID;
  lot: IdAsUUID;
  quantity: number;
  date: Date;
}

export interface RMAFormDataReceiveBatch {
  rma_data: RMAFormDataReceive[];
}

export const RMAValidationSchemaReceiveFull = yup.object({
  rma_data: yup
    .array()
    .of(RMAValidationSchemaReceive)
    .defined()
    // eslint-disable-next-line no-template-curly-in-string
    .min(1, "Must receive at least ${min} RMAs.")
    .required(),
});

export interface RMARequestDataReceiveFull {
  rma_data: RMARequestDataReceive[];
}

export const SERVICE_STATUSES = [
  "OPEN",
  "CLOSED",
  "VOID",
  "PROCESSING",
  "PRODUCTION_REVIEW",
  "QUALITY_REVIEW",
  "HOLDING",
] as const;
export type ServiceStatusType = (typeof SERVICE_STATUSES)[number];

export interface ServiceEventFull extends SCH_BaseFull {
  number: string;

  date_opened: string;
  date_started: string | null;
  date_completed: string | null;
  estimated_completion_date: string;

  details: string;
  resolution: string | null;
  status: ServiceStatusType;

  tgcm_id: IdAsUUID;
  rma_id: IdAsUUID;
}

export const ISSUE_TRACKER_CATEGORIES = [
  "PRODUCTION_DOCUMENTATION",
  "INCOMING_RECEIVING_INSPECTION",
  "NUCLEUS_ENTRY",
  "NETSUITE_ENTRY",
  "OTHER",
] as const;
export type IssueTrackerCategoryType =
  (typeof ISSUE_TRACKER_CATEGORIES)[number];

export interface IssueRecordCreate extends BaseCreateSchema {
  employee_id: IdAsUUID;
  reported_by_id: IdAsUUID;
  issue_category: IssueTrackerCategoryType;
  issue_occurrence: Date;
  issue_other: string | null;
  issue_description: string;
  supporting_document_ids: { [key: string]: string };
  linked_records: { [key: string]: TableObject<"linked_record"> };
}

export interface IssueRecordUpdate extends Partial<
  Omit<IssueRecordCreate, "reported_by_id">
> {}
